import { useCallback, useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import { useDutchieIntelligenceContext } from '../../context/dutchie-intelligence';

import { scanCharts } from './chart-scanners';
import {
  buildChartsContext,
  buildPageActionsContext,
  buildPageStructureContext,
  buildTableSelectionContext,
  buildVisibleTablesContext,
} from './context-builders';
import { setupDebugHelpers } from './debug-utils';
import {
  CHARTS_METADATA,
  DOM_CONTEXT_IDS,
  DOM_CONTEXT_SCOPES,
  INTERACTION_THROTTLE_DELAY,
  MUTATION_THROTTLE_DELAY,
  PAGE_ACTIONS_METADATA,
  PAGE_STRUCTURE_METADATA,
  SCAN_DEBOUNCE_DELAY,
  TABLE_SELECTION_METADATA,
  VISIBLE_TABLES_METADATA,
} from './helpers';
import { scanPageActions, scanPageStructure } from './page-scanners';
import { scanTableSelections, scanVisibleTables } from './table-scanners';

import type { ContextMetadata } from '../../context/types';

/**
 * Automatic context observers that detect and register UI state without requiring
 * manual integration into components.
 *
 * This bridge uses DOM observation and event delegation to automatically capture:
 * - Page structure (headings, metrics)
 * - Visible tables and their data
 * - Table selections (via MUI DataGrid aria attributes)
 * - Page action buttons (via data attributes or common patterns)
 * - Chart data and trends (Chart.js via react-chartjs-2)
 *
 * NO COMPONENT MODIFICATIONS REQUIRED - works automatically!
 */
export function DOMObserverResource() {
  const { setComponentContext, removeComponentContext } = useDutchieIntelligenceContext();
  const location = useLocation();
  const observerRef = useRef<MutationObserver | null>(null);

  // Cache to prevent unnecessary updates when data hasn't changed
  const lastContextCache = useRef<Record<string, string | undefined>>({});

  /**
   * Helper to update context only if it has changed
   */
  const updateContextIfChanged = useCallback(
    (contextId: string, newContext: string | null, prefix?: string, metadata?: Partial<ContextMetadata>) => {
      // Don't update if context is null/empty
      if (!newContext) {
        return;
      }

      const lastContext = lastContextCache.current[contextId];
      if (lastContext !== newContext) {
        lastContextCache.current[contextId] = newContext;
        setComponentContext(contextId, newContext, prefix, metadata);
      }
    },
    [setComponentContext]
  );

  /**
   * Scan for table selections and register context
   */
  const scanAndRegisterTableSelections = useCallback(() => {
    try {
      const tableData = scanTableSelections(location.pathname);
      const contextString = buildTableSelectionContext(tableData);

      if (!contextString) {
        // Clean up any existing table selection contexts and cache
        lastContextCache.current[DOM_CONTEXT_IDS.TABLE_SELECTION] = undefined;
        removeComponentContext(DOM_CONTEXT_IDS.TABLE_SELECTION);
        return;
      }

      updateContextIfChanged(
        DOM_CONTEXT_IDS.TABLE_SELECTION,
        contextString,
        DOM_CONTEXT_SCOPES.TABLE_SELECTION,
        TABLE_SELECTION_METADATA
      );
    } catch (error) {
      console.warn('[DOMObserverResource] Table scan failed:', error);
    }
  }, [updateContextIfChanged, removeComponentContext, location.pathname]);

  /**
   * Scan for page structure and register context
   */
  const scanAndRegisterPageStructure = useCallback(() => {
    try {
      const pageStructure = scanPageStructure(location.pathname);
      const contextString = buildPageStructureContext(pageStructure);

      if (!contextString) {
        return;
      }

      updateContextIfChanged(
        DOM_CONTEXT_IDS.PAGE_STRUCTURE,
        contextString,
        DOM_CONTEXT_SCOPES.PAGE,
        PAGE_STRUCTURE_METADATA
      );
    } catch (error) {
      console.warn('[DOMObserverResource] Page structure scan failed:', error);
    }
  }, [updateContextIfChanged, location.pathname]);

  /**
   * Scan for visible tables and register context
   */
  const scanAndRegisterVisibleTables = useCallback(() => {
    try {
      const tableData = scanVisibleTables();
      const contextString = buildVisibleTablesContext(tableData);

      if (!contextString) {
        lastContextCache.current[DOM_CONTEXT_IDS.VISIBLE_TABLES] = undefined;
        removeComponentContext(DOM_CONTEXT_IDS.VISIBLE_TABLES);
        return;
      }

      updateContextIfChanged(
        DOM_CONTEXT_IDS.VISIBLE_TABLES,
        contextString,
        DOM_CONTEXT_SCOPES.PAGE,
        VISIBLE_TABLES_METADATA
      );
    } catch (error) {
      console.warn('[DOMObserverResource] Visible tables scan failed:', error);
    }
  }, [updateContextIfChanged, removeComponentContext]);

  /**
   * Scan for page actions and register context
   */
  const scanAndRegisterPageActions = useCallback(() => {
    try {
      const actionsData = scanPageActions(location.pathname);
      const contextString = buildPageActionsContext(actionsData);

      if (!contextString) {
        return;
      }

      updateContextIfChanged(
        DOM_CONTEXT_IDS.PAGE_ACTIONS,
        contextString,
        DOM_CONTEXT_SCOPES.PAGE,
        PAGE_ACTIONS_METADATA
      );
    } catch (error) {
      console.warn('[DOMObserverResource] Actions scan failed:', error);
    }
  }, [updateContextIfChanged, location.pathname]);

  /**
   * Scan for charts and register context
   */
  const scanAndRegisterCharts = useCallback(() => {
    try {
      const chartsData = scanCharts();
      const contextString = buildChartsContext(chartsData);

      if (!contextString) {
        lastContextCache.current[DOM_CONTEXT_IDS.CHARTS] = undefined;
        removeComponentContext(DOM_CONTEXT_IDS.CHARTS);
        return;
      }

      updateContextIfChanged(DOM_CONTEXT_IDS.CHARTS, contextString, DOM_CONTEXT_SCOPES.PAGE, CHARTS_METADATA);
    } catch (error) {
      console.warn('[DOMObserverResource] Charts scan failed:', error);
    }
  }, [updateContextIfChanged, removeComponentContext]);

  /**
   * Run all scans with debouncing
   *
   * Debouncing prevents excessive DOM scanning during rapid user interactions or DOM mutations.
   * Without this, typing "hello" would trigger 5 scans, or rapid clicking would trigger dozens.
   * Instead, we wait for a period of "silence" before executing scans, reducing CPU usage and improving performance.
   */
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const runAllScans = useCallback(() => {
    // Clear any pending scan and restart the timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only execute scans after configured delay of no new calls
    debounceTimerRef.current = setTimeout(() => {
      scanAndRegisterPageStructure();
      scanAndRegisterVisibleTables();
      scanAndRegisterTableSelections();
      scanAndRegisterPageActions();
      scanAndRegisterCharts();
    }, SCAN_DEBOUNCE_DELAY);
  }, [
    scanAndRegisterPageStructure,
    scanAndRegisterVisibleTables,
    scanAndRegisterTableSelections,
    scanAndRegisterPageActions,
    scanAndRegisterCharts,
  ]);

  // Set up DOM observer to detect changes
  useEffect(() => {
    // Initial scan
    runAllScans();

    // Set up mutation observer to detect DOM changes (throttled)
    let mutationTimer: ReturnType<typeof setTimeout> | null = null;
    observerRef.current = new MutationObserver((mutations) => {
      // Filter out mutations from the intelligence banner to prevent feedback loops
      const relevantMutations = mutations.filter((mutation) => {
        // Check if the mutation target or any parent is the intelligence banner
        const node = mutation.target as HTMLElement;
        // Skip mutations from intelligence summary banners
        // closest() checks the element itself first, then ancestors
        if (node.closest?.('[data-intelligence-banner="true"]')) {
          return false;
        }
        return true;
      });

      // If no relevant mutations, skip scanning
      if (relevantMutations.length === 0) {
        return;
      }

      // Throttle mutations - only run scans if 2 seconds have passed
      if (mutationTimer) {
        return;
      }
      mutationTimer = setTimeout(() => {
        mutationTimer = null;
        runAllScans();
      }, MUTATION_THROTTLE_DELAY);
    });

    // Observe the entire app container with more specific filters
    const appContainer = document.getElementById('app-container');
    if (appContainer) {
      observerRef.current.observe(appContainer, {
        childList: true,
        subtree: true, // TODO: Make this a prop? // Observe all children and their children
        attributes: true,
        attributeFilter: ['aria-selected'], // Only watch selection changes
      });
    } else {
      console.warn('[DOMObserverResource] app-container not found! Observer will not work.');
    }

    // Listen to common user interactions (throttled)
    let interactionTimer: ReturnType<typeof setTimeout> | null = null;
    const handleInteraction = () => {
      if (interactionTimer) {
        return;
      }
      interactionTimer = setTimeout(() => {
        interactionTimer = null;
        runAllScans();
      }, INTERACTION_THROTTLE_DELAY);
    };
    document.addEventListener('click', handleInteraction, { capture: true, passive: true });
    document.addEventListener('change', handleInteraction, { passive: true });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('change', handleInteraction);
    };
  }, [runAllScans]);

  // Re-scan when route changes
  useEffect(() => {
    runAllScans();
  }, [location.pathname, runAllScans]);

  // Debug helper - expose to window in development
  useEffect(() => {
    const cleanup = setupDebugHelpers(runAllScans);
    return cleanup;
  }, [runAllScans]);

  return null;
}
